import { Suspense, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { BackArrow } from 'src/shared/components/icons/back-arrow'
import { Button } from 'src/shared/components/ui/buttons/button'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'

import { usePortfolioItems } from '../../../api/portfolio/use-portfolio-items'
import type { PortfolioItem } from '../../../schemas/portfolio'
import NoPortfolio from '../empty/no-portfolio'

import PortfolioCard from './portfolio-card'
import PortfolioListSkeleton from './portfolio-list-skeleton'

function PortfolioListMobile({
  portfolio,
  isRealtor,
  userId,
  username,
}: {
  portfolio: PortfolioItem[]
  isRealtor: boolean
  userId: number
  username: string
}) {
  return (
    <div
      className="-mx-5 snap-x overflow-x-auto pb-4
        [&::-webkit-scrollbar-thumb]:rounded-full
      [&::-webkit-scrollbar-thumb]:bg-[#1B3029]
        [&::-webkit-scrollbar-track]:mx-4
        [&::-webkit-scrollbar-track]:rounded-full
      [&::-webkit-scrollbar-track]:bg-[#D8D8D6]
        [&::-webkit-scrollbar]:hidden
        [&::-webkit-scrollbar]:h-[5px]
        xs:[&::-webkit-scrollbar]:block
        xs:[&::-webkit-scrollbar]:h-[10px]
    "
    >
      <div className="flex pl-5 xs:gap-4">
        {portfolio.map((item, index) => (
          <div
            key={item.id}
            className={cn('snap-start pl-4', index === 0 && 'pl-5', index === 0 && portfolio.length === 1 && 'pl-0')}
          >
            <PortfolioCard
              userId={userId}
              username={username}
              item={item}
              isRealtor={isRealtor}
              className="w-4/5 min-w-[280px] shrink-0"
            />
          </div>
        ))}
        {/* This is a hack to add padding after the last item */}
        <div className="shrink-0 pl-5"></div>
      </div>
    </div>
  )
}

type PortfolioListProps = {
  username: string
  profession: string
  onGoBack?: () => void
  showAll?: boolean
  mobile?: boolean
  userId: number
  firstName: string
}

const PortfolioListContent = ({
  userId,
  username,
  profession,
  onGoBack,
  showAll = false,
  // mobile,
  firstName,
}: PortfolioListProps) => {
  const navigate = useNavigate()
  const isRealtor = profession === 'real_estate_agent'
  const { data: portfolioData } = usePortfolioItems(userId)

  const { isMobile } = useBreakpoint()

  showAll = true
  // mobile = false

  const hasMorePortfolio = (portfolioData?.length || 0) > 4

  const portfolio = useMemo(() => {
    if (showAll) {
      return portfolioData
    }

    return portfolioData.slice(0, 4)
  }, [portfolioData, showAll])

  const showAllButtonOnClick = () => {
    navigate(paths.users.publicProfile.portfolio.getHref(username))
  }

  return (
    <section className="mb-6">
      {/* {(isRealtor || !mobile) && (
        <SectionTitle
          onGoBack={onGoBack}
          title={'Portfolio'}
          rightContent={
            !mobile &&
            !showAll &&
            portfolio.length > 0 && (
              <Button
                variant="default"
                size="default"
                className="w-[140px] rounded-full bg-primary text-[14px] font-semibold text-white"
                onClick={showAllButtonOnClick}
              >
                Show all
              </Button>
            )
          }
        />
      )} */}

      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          {onGoBack && (
            <button
              onClick={onGoBack}
              className="flex size-6 items-center justify-center rounded-full border border-border bg-[#f8f7f5] text-primary md:size-10"
            >
              <BackArrow className="size-3 md:size-5" />
            </button>
          )}
          <h2 className="text-[18px] font-semibold leading-none tracking-[-0.18px] text-primary xs:text-[24px] xs:tracking-[-0.24px]">
            Portfolio
          </h2>
        </div>
        {!onGoBack && hasMorePortfolio && (
          <Button
            variant="default"
            size="sm"
            className="h-[35px] w-[98px] rounded-full bg-primary text-[12px] font-semibold text-white xs:h-[45px] xs:w-[140px] xs:text-[14px]"
            onClick={showAllButtonOnClick}
          >
            Show all
          </Button>
        )}
      </div>
      <div className="mt-8">
        {isMobile && !onGoBack ? (
          <PortfolioListMobile portfolio={portfolio} isRealtor={isRealtor} userId={userId} username={username} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-[repeat(auto-fill,minmax(290px,1fr))]">
            {portfolio.map((item) => (
              <PortfolioCard key={item.id} item={item} isRealtor={isRealtor} userId={userId} username={username} />
            ))}
          </div>
        )}
      </div>
      {portfolio.length === 0 && <NoPortfolio firstName={firstName} />}
      {/* {!showAll && portfolio.length > 0 && (
        <ViewAllButton
          text="Show all items"
          onClick={() => navigate(paths.users.publicProfile.portfolio.getHref(username))}
        />
      )} */}
    </section>
  )
}

const PortfolioList = ({
  userId,
  username,
  profession,
  onGoBack,
  showAll = false,
  mobile,
  firstName,
}: PortfolioListProps) => {
  return (
    <Suspense fallback={<PortfolioListSkeleton />}>
      <PortfolioListContent
        username={username}
        profession={profession}
        onGoBack={onGoBack}
        showAll={showAll}
        mobile={mobile}
        userId={userId}
        firstName={firstName}
      />
    </Suspense>
  )
}

export default PortfolioList
